import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Alert} from "@mui/material";
import {Add, Check, DeleteOutline, EditOutlined, LibraryAdd, Tune} from "@mui/icons-material";
import {useSitStores, useStores} from "../../../../stores";
import {SitRole} from "../../../ApiTypes";
import type {Lever, LeverGroup} from "../../../ApiTypes";
import {SitField, SitSection} from "../../../components/design/SitDesignAtoms";

const NEW_GROUP = -1;
const STANDARD_LEVERS = [
    'Volume Bundling (Pooling of Demand)', 'Price Evaluation', 'Extension of Supply Base', 'Product Optimization',
    'Process Optimization', 'Optimization of Supply Relationship', 'Category-Spanning Optimization',
];

type Draft = {id?: number; title: string; description: string; groupId: number; newGroup: string};
const emptyDraft = (groupId: number): Draft => ({title: '', description: '', groupId, newGroup: ''});

const errorText = (e: unknown): string => {
    const data = (e as {response?: {data?: unknown}})?.response?.data;
    if (data && typeof data === 'object') {
        const values = Object.values(data as Record<string, unknown>).flat();
        if (values.length) return values.map(String).join(' ');
    }
    return 'Could not save. Try again.';
};

/** Settings section: the levers initiatives are assigned to, in their groups. Only SIT admins change them. */
export const LeverSettings: React.FC = observer(() => {
    const {authStore} = useStores();
    const {ccStore, sitApi} = useSitStores();
    const isAdmin = authStore.sitUserProfile?.role === SitRole.ADMIN;
    const levers: Lever[] = ccStore.leverRM.result ?? [];
    const groups: LeverGroup[] = ccStore.leverGroupRM.result ?? [];
    const [draft, setDraft] = useState<Draft>(emptyDraft(NEW_GROUP));
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // The configuration store loads levers once; this page needs their current use counts.
        ccStore.leverRM.request();
        ccStore.leverGroupRM.request();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (draft.groupId === NEW_GROUP && !draft.id && groups.length) setDraft(d => ({...d, groupId: groups[0].id}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups.length]);

    const run = (action: () => Promise<unknown>, after?: () => void) => {
        setBusy(true);
        setError(null);
        action()
            .then(() => {
                ccStore.leverRM.request();
                ccStore.leverGroupRM.request();
                after?.();
            })
            .catch(e => setError(errorText(e)))
            .finally(() => setBusy(false));
    };

    const save = () => run(async () => {
        let groupId = draft.groupId;
        if (groupId === NEW_GROUP) groupId = (await sitApi.saveLeverGroup(draft.newGroup.trim())).data.id;
        await sitApi.saveLever({title: draft.title.trim(), description: draft.description.trim(), group_id: groupId}, draft.id);
    }, () => setDraft(emptyDraft(draft.groupId === NEW_GROUP ? groups[0]?.id ?? NEW_GROUP : draft.groupId)));

    const remove = (lever: Lever) => {
        if (window.confirm(`Delete the lever "${lever.title}"?`)) run(() => sitApi.deleteLever(lever.id));
    };
    const renameGroup = (group: LeverGroup) => {
        const title = window.prompt('Group name', group.title);
        if (title && title.trim() !== group.title) run(() => sitApi.saveLeverGroup(title.trim(), group.id));
    };
    const removeGroup = (group: LeverGroup) => {
        if (window.confirm(`Delete the group "${group.title}"?`)) run(() => sitApi.deleteLeverGroup(group.id));
    };
    const addStandard = () => run(() => sitApi.addDefaultLevers());

    const ready = draft.title.trim() && (draft.groupId !== NEW_GROUP || draft.newGroup.trim());
    const usedBy = (n?: number | null) => n ? `${n} initiative${n === 1 ? '' : 's'} or target${n === 1 ? '' : 's'}` : '—';
    const standardButton = isAdmin && (
        <button type="button" className="ac-btn" disabled={busy} onClick={addStandard}
                title={`Adds the missing ones of: ${STANDARD_LEVERS.join(', ')}`}>
            <LibraryAdd fontSize="small"/> Add the standard levers
        </button>
    );

    return (
        <form className="sit-settings-block" onSubmit={(event) => { event.preventDefault(); if (ready) save(); }}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {!isAdmin && <Alert severity="info">Only Savings Initiative Tracker admins can change levers.</Alert>}

            {isAdmin && levers.length === 0 && ccStore.leverRM.result && (
                <SitSection icon={<LibraryAdd/>} title="No levers yet" tint="c"
                            sub="Initiatives need a lever. Start with the standard procurement levers, then add your own.">
                    <ul className="sit-lever-standard">
                        {STANDARD_LEVERS.map(l => <li key={l}>{l}</li>)}
                    </ul>
                    {standardButton}
                </SitSection>
            )}

            {isAdmin && (
                <SitSection icon={draft.id ? <EditOutlined/> : <Add/>} tint="a"
                            title={draft.id ? 'Edit lever' : 'Add a lever'}
                            sub="Initiatives are assigned to a lever; levers are grouped for reporting.">
                    <div className="sit-lever-form">
                        <SitField label="Group" req>
                            <select value={draft.groupId}
                                    onChange={e => setDraft({...draft, groupId: Number(e.target.value)})}>
                                {groups.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                                <option value={NEW_GROUP}>New group…</option>
                            </select>
                        </SitField>
                        {draft.groupId === NEW_GROUP && (
                            <SitField label="New group name" req>
                                <input value={draft.newGroup} maxLength={128} placeholder="e.g. Procurement"
                                       onChange={e => setDraft({...draft, newGroup: e.target.value})}/>
                            </SitField>
                        )}
                        <SitField label="Lever" req>
                            <input value={draft.title} maxLength={256} placeholder="e.g. Price Evaluation"
                                   onChange={e => setDraft({...draft, title: e.target.value})}/>
                        </SitField>
                        <SitField label="Description" full>
                            <input value={draft.description} maxLength={256}
                                   onChange={e => setDraft({...draft, description: e.target.value})}/>
                        </SitField>
                    </div>
                    <div className="sit-lever-actions">
                        {draft.id && (
                            <button type="button" className="ac-btn" disabled={busy}
                                    onClick={() => setDraft(emptyDraft(groups[0]?.id ?? NEW_GROUP))}>Cancel</button>
                        )}
                        <button type="submit" className="ac-btn ac-btn--primary" disabled={!ready || busy}>
                            <Check fontSize="small"/> {draft.id ? 'Save lever' : 'Add lever'}
                        </button>
                    </div>
                </SitSection>
            )}

            <SitSection icon={<Tune/>} title="Levers" tint="b" right={levers.length > 0 && standardButton}>
                <div className="sit-conv-table">
                    <table className="sit-table">
                        <thead>
                            <tr>
                                <th>Group</th>
                                <th>Lever</th>
                                <th>Description</th>
                                <th>Used by</th>
                                {isAdmin && <th/>}
                            </tr>
                        </thead>
                        <tbody>
                            {levers.map(l => (
                                <tr key={l.id}>
                                    <td>{l.group.title}</td>
                                    <td><b>{l.title}</b></td>
                                    <td>{l.description}</td>
                                    <td>{usedBy(l.in_use)}</td>
                                    {isAdmin && (
                                        <td className="right sit-lever-rowactions">
                                            <button type="button" className="ac-icon-btn" title={`Edit ${l.title}`}
                                                    onClick={() => setDraft({id: l.id, title: l.title, description: l.description,
                                                        groupId: l.group.id, newGroup: ''})}>
                                                <EditOutlined fontSize="small"/>
                                            </button>
                                            <button type="button" className="ac-icon-btn" disabled={busy || Boolean(l.in_use)}
                                                    title={l.in_use ? 'In use: it cannot be deleted' : `Delete ${l.title}`}
                                                    onClick={() => remove(l)}>
                                                <DeleteOutline fontSize="small"/>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {levers.length === 0 && (
                                <tr><td colSpan={isAdmin ? 5 : 4} className="sit-muted">No levers yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SitSection>

            {isAdmin && groups.length > 0 && (
                <SitSection icon={<Tune/>} title="Groups" tint="plain"
                            sub="A group can be deleted once no lever or cluster uses it.">
                    <div className="sit-conv-table">
                        <table className="sit-table">
                            <tbody>
                                {groups.map(g => (
                                    <tr key={g.id}>
                                        <td><b>{g.title}</b></td>
                                        <td>{g.in_use ? `${g.in_use} in use` : 'Not used'}</td>
                                        <td className="right sit-lever-rowactions">
                                            <button type="button" className="ac-icon-btn" title={`Rename ${g.title}`}
                                                    disabled={busy} onClick={() => renameGroup(g)}>
                                                <EditOutlined fontSize="small"/>
                                            </button>
                                            <button type="button" className="ac-icon-btn" disabled={busy || Boolean(g.in_use)}
                                                    title={g.in_use ? 'In use: it cannot be deleted' : `Delete ${g.title}`}
                                                    onClick={() => removeGroup(g)}>
                                                <DeleteOutline fontSize="small"/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </SitSection>
            )}
        </form>
    );
});
